<template>
  <div class="mt-6">
    <!-- Header con título y botón -->
    <div class="flex items-center justify-between mb-4">
      <h2
        class="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2"
      >
        {{ title }}
      </h2>
      <button
        type="button"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-800 focus:z-10 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/40 dark:hover:text-blue-300 transition-colors"
        @click="toggleSeleccionarTodo"
      >
        <CheckIcon
          v-if="modelValue.length < entidadesFiltradas.length"
          class="w-3 h-3"
        />
        <XMarkIcon v-else class="w-3 h-3" />
        {{
          modelValue.length < entidadesFiltradas.length
            ? 'Seleccionar todo'
            : 'Deseleccionar todo'
        }}
      </button>
    </div>

    <!-- Input de filtro y contador -->
    <div class="flex items-center gap-4 mb-4">
      <div class="flex-1">
        <InputDefault
          v-model="filtro"
          type="text"
          placeholder="Buscar entidades..."
          pre-icon="MagnifyingGlassIcon"
          :clear="true"
          size="default"
        />
      </div>

      <!-- Contador de entidades -->
      <div class="shrink-0 text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-lg">
        <span class="font-medium">{{ entidadesFiltradas.length }}</span> mostradas
        <span class="mx-1">•</span>
        <span class="font-medium text-blue-600 dark:text-blue-400">{{ modelValue.length }}</span> seleccionadas
      </div>
    </div>    <!-- Grid de entidades compacto -->
    <div
      :class="gridClasses"
    >
      <label
        v-for="entidad in entidadesFiltradas"
        :key="entidad.coenCodigo || entidad.coenId || Math.random()"
        class="relative flex items-center p-2.5 bg-gray-50 dark:bg-gray-800/50 border border-gray-400 dark:border-gray-700 rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        :class="{
          'ring-1 ring-blue-500 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-600':
            modelValue.includes(entidad.coenCodigo || '')
        }"
      >
        <input
          :model-value="modelValue"
          type="checkbox"
          :value="entidad.coenCodigo"
          class="sr-only"
          @change="toggleEntidad(entidad.coenCodigo || '')"
        />

        <!-- Checkbox visual personalizado compacto -->
        <div
          class="shrink-0 w-4 h-4 border-2 border-gray-400 dark:border-gray-600 rounded flex items-center justify-center mr-2.5 transition-colors"
          :class="{
            'bg-blue-600 border-blue-600': modelValue.includes(
              entidad.coenCodigo || ''
            ),
            'bg-white dark:bg-gray-700': !modelValue.includes(
              entidad.coenCodigo || ''
            )
          }"
        >
          <CheckIcon
            v-if="modelValue.includes(entidad.coenCodigo || '')"
            class="w-2.5 h-2.5 text-white"
          />
        </div>

        <!-- Contenido de la entidad compacto -->
        <div class="flex-1 min-w-0">
          <div
            class="text-xs font-medium text-gray-900 dark:text-gray-100 truncate"
          >
            {{ entidad.coenNombre }}
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400 truncate">
            {{ entidad.coenCodigo }}
          </div>
        </div>

        <!-- Indicador visual de selección más pequeño -->
        <div
          v-if="modelValue.includes(entidad.coenCodigo || '')"
          class="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-blue-600 rounded-full"
        ></div>
      </label>
    </div>

    <!-- Mensaje de error mejorado -->
    <div
      v-if="showError && !modelValue.length"
      class="flex items-center gap-2 p-3 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-400 mt-4"
    >
      <XCircleIcon class="shrink-0 w-4 h-4" />
      <span>{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { CheckIcon, XMarkIcon, XCircleIcon } from '@heroicons/vue/24/outline'
import InputDefault from '@/modules/Core/components/Form/InputDefault.vue'

interface IEntidad {
  coenId?: number | null
  coenCodigo?: string | null
  coenNombre?: string | null
}

interface Props {
  entidades: IEntidad[]
  modelValue: string[]
  title?: string
  showError?: boolean
  errorMessage?: string
  gridCols?: {
    base?: number
    sm?: number
    lg?: number
    xl?: number
    '2xl'?: number
  }
}

interface Emits {
  (event: 'update:modelValue', value: string[]): void
  (event: 'selectionChanged', value: string[]): void
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Entidades',
  showError: false,
  errorMessage: 'Debe seleccionar al menos una entidad para continuar',
  gridCols: () => ({
    base: 2,
    sm: 3,
    lg: 4,
    xl: 5,
    '2xl': 6
  })
})

const emit = defineEmits<Emits>()

// Estado reactivo para el filtro
const filtro = ref('')

// Computed para entidades filtradas
const entidadesFiltradas = computed(() => {
  if (!filtro.value.trim()) {
    return props.entidades
  }

  const filtroLower = filtro.value.toLowerCase().trim()
  return props.entidades.filter(entidad => {
    const nombre = entidad.coenNombre?.toLowerCase() || ''
    const codigo = entidad.coenCodigo?.toLowerCase() || ''
    return nombre.includes(filtroLower) || codigo.includes(filtroLower)
  })
})

const gridClasses = computed(() => {
  const { base, sm, lg, xl } = props.gridCols
  const xl2 = props.gridCols['2xl']

  return [
    'grid gap-2 mb-4',
    `grid-cols-${base}`,
    `sm:grid-cols-${sm}`,
    `lg:grid-cols-${lg}`,
    `xl:grid-cols-${xl}`,
    `2xl:grid-cols-${xl2}`
  ].join(' ')
})

const toggleEntidad = (codigoCodigo: string) => {
  const nuevaSeleccion = props.modelValue.includes(codigoCodigo)
    ? props.modelValue.filter(codigo => codigo !== codigoCodigo)
    : [...props.modelValue, codigoCodigo]

  emit('update:modelValue', nuevaSeleccion)
  emit('selectionChanged', nuevaSeleccion)
}

const toggleSeleccionarTodo = () => {
  const todasSeleccionadas = props.modelValue.length === entidadesFiltradas.value.length

  const nuevaSeleccion = todasSeleccionadas
    ? [] // Si todas están seleccionadas, deseleccionar todo
    : entidadesFiltradas.value
        .filter(item => item.coenCodigo)
        .map(item => item.coenCodigo!) // Si no todas están seleccionadas, seleccionar todo

  emit('update:modelValue', nuevaSeleccion)
  emit('selectionChanged', nuevaSeleccion)
}
</script>
